import { useState } from "react";
import { CalendarCheck, Clock, Phone } from "lucide-react";
import {
  CLINIC_HOURS,
  CLINIC_PHONE_DISPLAY,
  CLINIC_WHATSAPP_NUMBER,
  SERVICES,
  TIME_SLOTS,
} from "../config";
import { Reveal, SectionLabel } from "../components/Reveal";

const inputCls =
  "w-full rounded-2xl border border-[#DCE4E8] bg-white px-4 py-3.5 text-sm text-[#0f2b2e] outline-none transition-all duration-300 placeholder:text-[#8A9CAB]/70 focus:border-[#4CA6AF] focus:ring-4 focus:ring-[#4CA6AF]/15";

const Field = ({ label, required, error, children, testid }) => (
  <div data-testid={testid}>
    <label className="mb-1.5 block text-xs font-bold tracking-[0.15em] uppercase text-[#5f7482]">
      {label} {required && <span className="text-[#d06060]">*</span>}
    </label>
    {children}
    {error && (
      <p className="mt-1.5 text-xs font-semibold text-[#d06060]" data-testid={`${testid}-error`}>
        {error}
      </p>
    )}
  </div>
);

export default function BookAppointment() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    date: "",
    time: "",
    service: "General Checkup",
    consent: false,
  });
  const [errors, setErrors] = useState({});

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const submit = (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = "Please enter the patient's name";
    const digits = form.phone.replace(/\D/g, "").replace(/^91(?=\d{10}$)/, "");
    if (!/^\d{10}$/.test(digits)) errs.phone = "Enter a valid 10-digit mobile number";
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) errs.email = "Enter a valid email address";
    if (!form.date) errs.date = "Please pick a preferred date";
    if (!form.time) errs.time = "Please pick a preferred time";
    if (!form.consent) errs.consent = "Consent is required to confirm your appointment";
    setErrors(errs);
    if (Object.keys(errs).length) return;

    const msg = [
      "Hello! Dr. Vishwa's Dental Hospital ! I would like to confirm my appointment,",
      `Name : ${form.name.trim()}`,
      `Phone : ${form.phone.trim()}`,
      `Date : ${form.date}`,
      `Time : ${form.time}`,
      `Service : ${form.service}`,
    ].join("\n");
    window.open(
      `https://wa.me/${CLINIC_WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`,
      "_blank",
      "noopener"
    );
  };

  const today = new Date().toISOString().split("T")[0];

  return (
    <main>
      <section className="bg-[#f7fafb] pb-16 pt-28 sm:pb-20 sm:pt-36" data-testid="booking-page">
        <div className="mx-auto grid max-w-7xl items-start gap-10 px-6 sm:px-8 lg:grid-cols-2 lg:gap-16">
          <Reveal>
            <SectionLabel number="11" text="Book Appointment" />
            <h1 className="text-2xl font-bold leading-snug text-[#14717A] sm:text-3xl">
              Want to Get a Healthier Smile? Schedule Your Visit Today
            </h1>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-[#5f7482] sm:text-lg">
              Tell us a little about what you need — our team will confirm your
              appointment shortly. No waiting on hold.
            </p>
            <div className="mt-10 space-y-5">
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-[#14717A] shadow-sm">
                  <Clock className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm font-bold text-[#22444a]">Open Daily</p>
                  <p className="text-sm text-[#8A9CAB]">{CLINIC_HOURS}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-[#14717A] shadow-sm">
                  <Phone className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm font-bold text-[#22444a]">Prefer to Call?</p>
                  <a href={`tel:+${CLINIC_WHATSAPP_NUMBER}`} className="text-sm text-[#8A9CAB] transition-colors duration-200 hover:text-[#14717A]">
                    {CLINIC_PHONE_DISPLAY}
                  </a>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <form
              onSubmit={submit}
              data-testid="booking-form"
              className="rounded-3xl border border-[#DCE4E8] bg-white p-7 shadow-[0_24px_60px_rgba(20,113,122,0.10)] sm:p-10"
              noValidate
            >
              <div className="flex items-center gap-3">
                <CalendarCheck className="h-6 w-6 text-[#4CA6AF]" />
                <h2 className="font-heading text-2xl font-semibold text-[#0f2b2e]">Appointment Details</h2>
              </div>
              <div className="mt-7 space-y-5">
                <Field label="Patient Name" required error={errors.name} testid="field-name">
                  <input
                    type="text"
                    data-testid="input-name"
                    className={inputCls}
                    placeholder="Full name"
                    value={form.name}
                    onChange={(e) => set("name", e.target.value)}
                  />
                </Field>
                <Field label="Mobile Number" required error={errors.phone} testid="field-phone">
                  <input
                    type="tel"
                    data-testid="input-phone"
                    className={inputCls}
                    placeholder="10-digit mobile number"
                    value={form.phone}
                    onChange={(e) => set("phone", e.target.value)}
                  />
                </Field>
                <Field label="Email (Optional)" error={errors.email} testid="field-email">
                  <input
                    type="email"
                    data-testid="input-email"
                    className={inputCls}
                    placeholder="you@example.com"
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                  />
                </Field>
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Preferred Date" required error={errors.date} testid="field-date">
                    <input
                      type="date"
                      data-testid="input-date"
                      min={today}
                      className={inputCls}
                      value={form.date}
                      onChange={(e) => set("date", e.target.value)}
                    />
                  </Field>
                  <Field label="Preferred Time" required error={errors.time} testid="field-time">
                    <select
                      data-testid="input-time"
                      className={inputCls}
                      value={form.time}
                      onChange={(e) => set("time", e.target.value)}
                    >
                      <option value="">Select time</option>
                      {TIME_SLOTS.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </Field>
                </div>
                <Field label="Preferred Service" testid="field-service">
                  <select
                    data-testid="input-service"
                    className={inputCls}
                    value={form.service}
                    onChange={(e) => set("service", e.target.value)}
                  >
                    <option value="General Checkup">General Checkup</option>
                    {SERVICES.map((s) => (
                      <option key={s.slug} value={s.title}>{s.title}</option>
                    ))}
                  </select>
                </Field>
                <div data-testid="field-consent">
                  <label className="flex cursor-pointer items-start gap-3">
                    <input
                      type="checkbox"
                      data-testid="input-consent"
                      checked={form.consent}
                      onChange={(e) => set("consent", e.target.checked)}
                      className="mt-0.5 h-5 w-5 rounded accent-[#14717A]"
                    />
                    <span className="text-sm leading-relaxed text-[#5f7482]">
                      I consent to receive calls/messages as per the policy.
                    </span>
                  </label>
                  {errors.consent && (
                    <p className="mt-1.5 text-xs font-semibold text-[#d06060]" data-testid="field-consent-error">{errors.consent}</p>
                  )}
                </div>
                <button
                  type="submit"
                  data-testid="confirm-whatsapp-btn"
                  className="flex w-full items-center justify-center gap-3 rounded-full bg-[#25D366] px-8 py-4 text-sm font-bold text-white shadow-[0_12px_30px_rgba(37,211,102,0.35)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#1eb856]"
                >
                  <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                  </svg>
                  Confirm Appointment via WhatsApp
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
